/// <reference types="google.maps" />
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {RequestService} from "../../core/services/request/request.service";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {GoogleMap, MapInfoWindow, MapMarker, MapDirectionsService} from "@angular/google-maps";
import {GeographicLocation} from "./geographic-location";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription, BehaviorSubject} from 'rxjs';
import {GoogleMapsLoaderService} from "../../core/services/google-maps-loader/google-maps-loader.service";
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";

@Component({
  selector: 'sb-map-page',
  templateUrl: './map-page.component.html',
  styleUrls: ['./map-page.component.scss']
})
export class MapPageComponent implements OnInit, OnDestroy {

  apiLoaded = false;

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;

  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;

  // Public component data

  title = 'Map';

  // google-map component data

  mapZoom = 12;

  mapCenter: google.maps.LatLng = null;

  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 15,
    minZoom: 0,
  };

  markers: google.maps.Marker[] = [];
  markerOptions: google.maps.MarkerOptions[] = [];
  markerClickHandlers: any[] = [];

  // Infobox support

  infoboxTable: string = "";
  infoboxName: string = "";
  infoboxDataLabel = null;
  infoboxShowData = false;
  infoboxHeaders: string[] = [];
  infoboxRows: string[][] = [];

  infoBoxTravelTime: string = null;
  infoBoxTravelDistance: string = null;

  // Private data

  private geographicLocations: GeographicLocation[] = [];

  private canvas: any;
  private canvasContext: any;

  private activatedRouteSubscription: Subscription;

  private userRecords: any[] = [];
  private userRecordLocations: string[] = [];

  private locationRecords: any[] = [];

  private targetRecords: any[] = [];
  private targetRadius: number;
  private targetId: string = "";
  private targetTable: string = "";
  private targetPosition: google.maps.LatLng = null;
  private targetIcon: string = null;
  private targetRecordLocations: string[] = [];

  private advancedRecords: any = {};

  private queryParams: any = null;

  private metadata: any[] = null;

  readonly directionsResults$: BehaviorSubject<google.maps.DirectionsResult | undefined> = new BehaviorSubject(undefined);

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private requestService: RequestService,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private mapDirectionsService: MapDirectionsService,
              private googleMapsLoaderService: GoogleMapsLoaderService) {

    // const request: google.maps.DirectionsRequest = {
    //   destination: {lat: 30.21848811680641, lng: -98.7615023574802},
    //   origin: {lat: 30.20891230437097, lng: -98.97390421175226},
    //   travelMode: google.maps.TravelMode.DRIVING
    // };
    // this.directionsResults$ = this.mapDirectionsService.route(request).pipe(map(response => response.result));
  }

  async ngOnInit() {
    // google.maps.* cannot be referenced until this is complete
    this.apiLoaded = await this.googleMapsLoaderService.load();

    // Middle of the ocean
    this.mapCenter = new google.maps.LatLng(32.451871681568754, -119.69659014007884);

    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as angular page
    document.getElementById("detailMain")?.classList?.add("sb-map-page-content");

    // Text sizing support
    this.canvas = document.createElement("canvas");
    this.canvasContext = this.canvas.getContext("2d");

    // Find the record to display.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {
      this.targetId = params.get("id");
      this.targetTable = params.get("table");
      this.queryParams = (this.activatedRoute.queryParams as any).value; // Cheating way to do it.
      if (this.queryParams['radius']) {
        this.targetRadius = Number(this.queryParams['radius']);
      }
      if (this.queryParams['icon']) {
        this.targetIcon = this.queryParams['icon'];
      }
      if (this.queryParams['title']) {
        this.title = this.queryParams['title'];
      }

      await this.loadMetadata();
      await this.loadRecords();

      await this.getAdvancedData("Location");

      // this.plotLocations();

      this.plotAdvancedLocations();
      this.centreMap();
    });

  }

  ngOnDestroy(): void {
    this.activatedRouteSubscription && this.activatedRouteSubscription.unsubscribe();
  }

  async openInfoWindow(marker: MapMarker, record: any) {

    // Reset
    this.infoboxTable = null;
    this.infoboxName = null;
    this.infoboxShowData = false;
    this.infoboxDataLabel = null;
    this.infoboxHeaders = [];
    this.infoboxRows = [];
    this.infoBoxTravelDistance = null;
    this.infoBoxTravelTime = null;


    if (record && record.metadata) {
      this.infoboxShowData = true;
      this.infoboxHeaders = record.metadata.header;
      this.infoboxRows = [record.metadata.data];
      this.infoboxName = record.display;
      this.infoboxTable = record.table;

      if (record.metadata.plot_destination) {
        const dest = record.metadata.plot_destination;
        const request: google.maps.DirectionsRequest = {
          destination: {lat: dest.latitude, lng: dest.longitude},
          origin: {lat: record.latitude, lng: record.longitude},
          travelMode: google.maps.TravelMode.DRIVING
        };

        this.mapDirectionsService
          .route(request)
          .toPromise()
          .then(response => {
            if (response.status === "OK") {
              this.directionsResults$.next(response.result);

              let legData = response.result.routes[0].legs[0];

              this.infoBoxTravelDistance = legData.distance.text;
              this.infoBoxTravelTime = legData.duration.text;
            }
          });
        // this.directionsResults$ = this.mapDirectionsService.route(request).pipe(map(response => response.result));
      }
    } else if (record) {
      this.infoboxTable = record._tableClass;
      this.infoboxName = record?.Name?.displayValue || record?.Number?.displayValue;

      const data = {
        table: record._tableClass,
        id: record.id
      };

      const infoboxData = await this.getInfoboxData(data);
      if (infoboxData) {
        if (infoboxData.showData) {
          this.infoboxShowData = true;
          if (infoboxData.dataLabel) {
            this.infoboxDataLabel = infoboxData.dataLabel;
          }
          if (infoboxData.data) {
            this.infoboxHeaders = infoboxData.data.headers;
            this.infoboxRows = infoboxData.data.rows;
          }
        }
      }
    }

    this.infoWindow.open(marker);
  }

  private doPlot(latitude: number, longitude: number) {
    const useBounds = this.targetRadius && this.targetId && this.targetPosition;

    if (!useBounds) {
      return true;
    } else {
      return this.positionIsInCircle(new google.maps.LatLng(latitude, longitude), this.targetPosition, this.targetRadius);
    }
  }

  private plotAdvancedLocations() {
    this.markers.length = 0;
    this.markerOptions = [];

    let i = 0; // this.markers.length;

    Object.keys(this.advancedRecords).forEach(key => {
      this.advancedRecords[key].forEach((rec) => {
        if (rec.latitude && rec.longitude) {
          const {marker, markerOptions, markerClickHandler} = this.createMarker(rec, rec.display, rec.icon, rec.latitude, rec.longitude);

          this.markers.push(marker);
          this.markerOptions[i] = markerOptions;
          this.markerClickHandlers[i] = markerClickHandler;
          i++;
        }
      });
    });
  }

  private plotLocations() {
    this.markers.length = 0;
    this.markerOptions = [];

    const personIcon = "https://maps.google.com/mapfiles/kml/shapes/man.png";
    const generalIcon = "https://maps.google.com/mapfiles/kml/paddle/red-circle.png";

    let i = 0;
    this.targetRecords.forEach((u) => {
      const geolocation = this.geographicLocations.find((geo) => geo.id === u.id);
      if (geolocation && geolocation.latitude && geolocation.longitude) {
        const latitude = Number(geolocation.latitude);
        const longitude = Number(geolocation.longitude);
        if (this.doPlot(latitude, longitude)) {
          let icon = this.getIconFromMetadata(this.targetTable);
          if (!icon) {
            icon = (this.targetTable === 'User') ? personIcon : generalIcon;
          }
          if (this.targetIcon) {
            icon = this.targetIcon;
          }
          const {marker, markerOptions, markerClickHandler} =
            this.createMarker(u, u.Name.displayValue, icon, latitude, longitude);
          this.markers.push(marker);
          this.markerOptions[i] = markerOptions;
          this.markerClickHandlers[i] = markerClickHandler;
          i++;
        }
      }
    });

    this.userRecords.forEach((u) => {
      const geolocation = this.geographicLocations.find((geo) => geo.id === u.id);
      if (geolocation && geolocation.latitude && geolocation.longitude) {
        const latitude = Number(geolocation.latitude);
        const longitude = Number(geolocation.longitude);
        if (this.doPlot(latitude, longitude)) {
          let icon = this.getIconFromMetadata(this.targetTable);
          if (!icon) {
            icon = personIcon;
          }
          const {marker, markerOptions, markerClickHandler} =
            this.createMarker(u, u.Name.displayValue, icon, latitude, longitude);
          this.markers.push(marker);
          this.markerOptions[i] = markerOptions;
          this.markerClickHandlers[i] = markerClickHandler;
          i++;
        }
      }
    });

    // We don't plot locations that are used by the target records or user records as they will overlay the target record
    this.locationRecords.forEach((u) => {
      const geolocation = this.geographicLocations.find((geo) => geo.id === u.id);
      if (geolocation && geolocation.latitude && geolocation.longitude) {
        const latitude = Number(geolocation.latitude);
        const longitude = Number(geolocation.longitude);
        if (this.doPlot(latitude, longitude) && !this.targetRecordLocations.includes(u.id) && !this.userRecordLocations.includes(u.id)) {
          let icon = u?.Description?.value;
          if (!icon) {
            icon = this.getIconFromMetadata(this.targetTable);
          }
          if (!icon) {
            icon = generalIcon;
          }
          const {marker, markerOptions, markerClickHandler} =
            this.createMarker(u, u.Name.displayValue, icon, latitude, longitude);
          this.markers.push(marker);
          this.markerOptions[i] = markerOptions;
          this.markerClickHandlers[i] = markerClickHandler;
          i++;
        }
      }
    });
  }

  private createMarker(record: any, name: string, icon: string, latitude: number, longitude: number) {
    const position = {
      lat: latitude,
      lng: longitude
    };

    const marker = new google.maps.Marker({
      position: position,
      title: name, // tooltip
      label: name, // displayed on map
      clickable: true,
      animation: google.maps.Animation.DROP
    });

    const markerOptions = {
      animation: google.maps.Animation.DROP,
      icon: {
        url: icon,
        labelOrigin: new google.maps.Point(this.getLabelOffset(name) + 20, 12)
      }
    };

    const markerClickHandler = (target) => {
      this.openInfoWindow(target, record);
    };

    return { marker: marker, markerOptions: markerOptions, markerClickHandler: markerClickHandler }
  }

  private getLabelOffset(text: string) {
    this.canvasContext.font = "bold 14px Roboto";
    const metrics = this.canvasContext.measureText(text);
    return metrics.width;
  }

  private centreMap() {
    if (this.markers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach((m) => {
        bounds.extend(m.getPosition());
      });

      this.mapCenter = bounds.getCenter();

      this.map.fitBounds(bounds);
    }
  }

  private async loadMetadata() {
    this.metadata = await this.getRecords("MapMetadata");
  }

  private getIconFromMetadata(table: string) {
    const m = this.metadata.find((u) => u.Table.value === table);
    return m ? m.IconURL.value : null;
  }

  private async loadRecords() {
    try {
      let ids = this.targetId === 'All' ? [] : [this.targetId];
      this.targetRecords = await this.getRecords(this.targetTable, ids);

      this.targetRecords.forEach((u) => {
        if (u?.Location?.record?.id) {
          this.targetRecordLocations.push(u.Location.record.id);
        }
      });

      if (this.queryParams["User"] !== undefined) {
        const users = this.queryParams['User'];
        const ids = (users === "All") ? [] : users.replace(/ /g, '').split(',');
        this.userRecords = await this.getRecords('User', ids);
      }

      this.userRecords.forEach((u) => {
        if (u?.Location?.record?.id) {
          this.userRecordLocations.push(u.Location.record.id);
        }
      });

      if (this.queryParams["Location"] !== undefined) {
        const locations = this.queryParams['Location'];
        const ids = (locations === "All") ? [] : locations.replace(/ /g, '').split(',');
        this.locationRecords = await this.getRecords('Location', ids);
      }

      const targetIds = this.targetRecords.map((u) => {
        return {
          table: this.targetTable,
          id: u.id
        }
      });

      const userIds = this.userRecords.map((u) => {
        return {
          table: 'User',
          id: u.id
        }
      });

      const locationIds = this.locationRecords.map((u) => {
        return {
          table: 'Location',
          id: u.id
        }
      });

      const data = {
        ids: [...targetIds, ...userIds, ...locationIds]
      };

      this.geographicLocations = await this.getGeographicLocations(data);

      const targetPosition = this.geographicLocations.find( (u) => u.id === this.targetId);
      if (targetPosition) {
        this.targetPosition = new google.maps.LatLng(Number(targetPosition.latitude), Number(targetPosition.longitude));
      }
    } catch (e) {
      console.error(e);
    }
  }

  private async getGeographicLocations(data: any) {
    const request = this.requestService.createRequest()
      .controller("servicely.core.Map")
      .method('getGeographicLocations')
      .data(data);

    const response: any = await request.execute();

    return response.data;
  }

  private async getInfoboxData(data: any) {
    const request = this.requestService.createRequest()
      .controller("servicely.core.Map")
      .method('getInfoboxData')
      .data(data);

    const response: any = await request.execute();

    return response.data;
  }

  private async getAdvancedData(metaDataName: string) {
    const data = {
      id: this.targetId,
      targetTable: this.targetTable,
      name: metaDataName,
      radius: this.targetRadius
    };

    const request = this.requestService.createRequest()
      .controller("servicely.core.Map")
      .method("getAdvancedDataScript")
      .data(data);

    const response: any = await request.execute();

    this.advancedRecords = response.data;

    return response.data;
  }

  private async getRecords(tableName: string, ids: string[] = []) {
    try {
      const request = this.requestService.createRequest()
        .controller("servicely.core.System")
        .method("list")
        .table(tableName);

      if (ids.length > 0) {
        const controllerQuery = this.requestService.createControllerQuery();

        controllerQuery.query({
          and: [{
            fieldName: "id",
            operator: "in",
            value: ids
          }]
        });

        controllerQuery.firstResult(0);

        controllerQuery.maxResults(10000);

        request.controllerQuery(controllerQuery);
      }

      const response: any = await request.execute();

      return response.data;
    } catch (ex) {
      console.error(ex);
      return [];
    }
  }

  private positionIsInCircle(point: google.maps.LatLng, center: google.maps.LatLng, radius: number)
  {
    return (google.maps.geometry.spherical.computeDistanceBetween(point, center) <= radius)
  }

  // Infobox support


  // Fake button navigation

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  canGoBack() {
    return this.legacyNavigateService.canGoBack();
  }

  back() {
    this.legacyNavigateService.back();
  }

}
